import { Card, Css, formatDate, FormLines, StaticField, useTestIds } from "@homebound/beam";
import { PLAN_FALLBACK_IMG } from "src/routes/libraries/plan-package/takeoffs/utils";
import { assertNever, formatList } from "src/utils";
import { SelectConfigurationStep_StructureFragment } from "../1.SelectConfigurationStep.generated";
import {
  ApplyConfigurationCard_AggregateVersionFragment,
  ApplyConfigurationCard_CheckoutConfigFragment,
  ApplyConfigurationCard_ProductOfferingFragment,
  ApplyConfigurationCard_ProjectReadyPlanConfigFragment,
} from "./ApplyConfigurationCard.generated";

type ApplyConfigurationCardConfig =
  | ApplyConfigurationCard_CheckoutConfigFragment
  | ApplyConfigurationCard_ProjectReadyPlanConfigFragment;

type ApplyConfigurationCardProps = {
  structure: SelectConfigurationStep_StructureFragment;
  readyPlanConfig: ApplyConfigurationCardConfig;
  onClick?: () => void;
  onRemove?: () => void;
};

/** Displays a ready plan configuration's cover photo, plan and design selections. */
export function ApplyConfigurationCard(props: ApplyConfigurationCardProps) {
  const { structure, readyPlanConfig, onClick, onRemove } = props;
  const { name, updatedAt, readyPlan } = readyPlanConfig;
  const { aggregateVersion, productOffering, exteriorOptionNames, interiorOptionNames } = resolveConfigFields(
    structure,
    readyPlanConfig,
  );
  const tid = useTestIds({}, "applyConfigurationCard");
  const fields = [
    { label: "Plan", value: readyPlan?.name },
    { label: "Exterior", value: formatList(exteriorOptionNames) },
    { label: "Interior", value: formatList(interiorOptionNames) },
    { label: "Version", value: aggregateVersion?.displayVersion ?? "-" },
    { label: "Updated", value: formatDate(updatedAt.toPlainDate(), "shortDate") },
  ];
  return (
    <div onClick={onClick} css={Css.df.w100.mw0.marker.$}>
      <Card
        bordered={true}
        fullWidth={true}
        imageFit="cover"
        imgSrc={productOffering?.coverAsset?.previewUrl ?? PLAN_FALLBACK_IMG}
        subtitle={readyPlan?.code ?? ""}
        // `Card` renders its `title` string unclamped, so we render the name ourselves to keep a long one to one line
        title=""
        buttonMenuItems={onRemove ? [{ label: "Replace", icon: "trash", onClick: onRemove }] : undefined}
        detailContent={
          <div css={Css.df.fdc.gap1.$}>
            <div css={Css.smSb.lineClamp1.wbba.$} {...tid.name}>
              {name ?? "Current Configuration"}
            </div>
            <FormLines width="sm" labelStyle="left" gap={0}>
              {fields.map((field) => (
                <StaticField key={field.label} label={field.label} value={field.value ?? "-"} />
              ))}
            </FormLines>
          </div>
        }
        {...tid}
      />
    </div>
  );
}

/**
 * Resolves the aggregateVersion and productOffering fields for a configuration.
 * A checkout config covers both sides of the lot, so an ADU structure reads the ADU side of it.
 */
function resolveConfigFields(
  structure: SelectConfigurationStep_StructureFragment,
  readyPlanConfig: ApplyConfigurationCardConfig,
): {
  aggregateVersion: ApplyConfigurationCard_AggregateVersionFragment | undefined | null;
  productOffering: ApplyConfigurationCard_ProductOfferingFragment | undefined | null;
  exteriorOptionNames: string[];
  interiorOptionNames: string[];
} {
  switch (readyPlanConfig.__typename) {
    case "CheckoutConfig":
      if (structure.isAdu) {
        return {
          aggregateVersion: readyPlanConfig.aduAggregateVersion,
          productOffering: readyPlanConfig.aduProductOffering,
          exteriorOptionNames: readyPlanConfig.aduExteriorOptionNames,
          interiorOptionNames: readyPlanConfig.aduInteriorOptionNames,
        };
      } else {
        return {
          aggregateVersion: readyPlanConfig.aggregateVersion,
          productOffering: readyPlanConfig.productOffering,
          exteriorOptionNames: readyPlanConfig.mhExteriorOptionNames,
          interiorOptionNames: readyPlanConfig.mhInteriorOptionNames,
        };
      }
    case "ProjectReadyPlanConfig":
      return {
        aggregateVersion: readyPlanConfig.aggregateVersion,
        productOffering: readyPlanConfig.productOffering,
        exteriorOptionNames: readyPlanConfig.exteriorOptionNames,
        interiorOptionNames: readyPlanConfig.interiorOptionNames,
      };
    default:
      assertNever(readyPlanConfig.__typename as never);
  }
}
